import Backbone from "backbone";
import _ from "underscore";
import {UnitCollection} from "./unit-collection";
import {availabilityUtils, sort, rent} from "utils";
import {dedup, flatten} from "utils/arrays";

const FloorPlanModel = Backbone.Model.extend({
    availabilityHTML: function(applyText, useNewTab, useContactFormButton=false) {
        let link = this.getAvailabilityLink(),
            waitUnits = this.get("wait_units"),
            customWaitListLinkText = this.get("custom_wait_list_link_text");

        if (useContactFormButton) {
            return availabilityUtils.makeContactForm(applyText, this);
        }

        if (!link) {
            return "";
        }

        if (waitUnits && waitUnits.length && availabilityUtils.isCoreLogicLink(link)) {
            return availabilityUtils.makeCoreLogicForm(
                link,
                applyText,
                new Backbone.Model(waitUnits[0]),
                useNewTab
            );
        }

        return availabilityUtils.makeDefaultAvailLink(
            link,
            customWaitListLinkText || applyText,
            useNewTab
        );
    },
    toJSON: function() {
        let superJSON = Backbone.Model.prototype.toJSON,
            intermediate = superJSON.apply(this, arguments);

        intermediate.units = intermediate.units.toJSON();

        return intermediate;
    },
    parse: function(model, options) {
        let now = Date.now();

        let units = model.units.map(function(unit) {
            unit.floor_plan = model.name;
            unit.display_bed = model.display_bed;
            return unit;
        });

        model.units = new UnitCollection(units, options);
        model.availableCount = model.units.reduce(function(memo, unit) {
            if (unit.get("available_date") <= now) {
                return memo + 1;
            }
            return memo;
        }, 0);

        model.rentValues = rent.valuesFromString(model.rent);

        return model;
    },
    getPhotos: function() {
        let galleries = this.get("galleries");
        let photos = [];

        if (galleries) {
            _.each(galleries, function(gal) {
                // Sort by order then by name
                let sorted = _.sortBy(gal.photos, function(a, b) {
                    if (a.order !== b.order) {
                        return sort.numeric(a.order, b.order);
                    }
                    return sort.natural(a.name, b.name);
                });
                photos = photos.concat(sorted);
            });
        }

        return photos;
    },
    getArea: function() {
        const area = this.get("sq_ft"),
            isLastNumeric = /\d$/.test(area);

        return isLastNumeric ? `${area} sq ft` : area;
    },
    getHideUnitNumbers: function() {
        let community = this.get("community");
        let hideUnitNumbers = false;

        if (community) {
            hideUnitNumbers = community.settings.hide_unit_numbers;
        }

        return hideUnitNumbers;
    },
    getHideUnitAvailability: function() {
        const community = this.get("community");
        let hideUnitAvailability = false;

        if (community) {
            hideUnitAvailability = community.settings.hide_unit_availability;
        }

        return hideUnitAvailability;
    },
    getTour: function() {
        let tours = this.get("tours");
        if (tours.hasOwnProperty("3D Tour")) {
            return tours["3D Tour"];
        }
        if (tours.hasOwnProperty("360 Tour")) {
            return tours["360 Tour"];
        }
        return null;
    },
    getGalleryTour: function() {
        const tours = this.get("tours");
        return tours["Gallery"];
    },
    hasGalleryTour: function() {
        return !!this.getGalleryTour();
    },
    has3D: function() {
        if (this.hasPanorama() || this.getTour() || this.hasFlythrough()) {
            return true;
        }
        return false;
    },
    hasFlythrough: function() {
        let flythrough = this.get("flythrough");
        return !!(flythrough && flythrough.image);
    },
    hasPanorama: function() {
        let panorama = this.get("panorama");
        return !!(panorama && panorama.is_enabled);
    },
    hasUnitPromo: function() {
        let units = this.getUnits();

        for (let i = 0; i < units.length; i++) {
            if (units[i].promo()) {
                return true;
            }
        }

        return false;
    },
    getAvailableDate: function() {
        const prop = "available_date";

        let units = this.get("units");

        return units.length ? units.min(prop).get(prop) : new Date(NaN);
    },
    getMinimumRent: function() {
        let min = Infinity;

        this.get("units").forEach(function(unit) {
            let values = unit.get("rentValues");
            if (values.length && values[0] < min) {
                min = values[0];
            }
        });

        return min;
    },
    filterDate: function(d) {
        return this.get("units").some((u) => u.get("available_date") <= d);
    },
    hasUnitNumber: function(number) {
        var i,
            units = this.get("units").toArray();

        for (i = 0; i < units.length; i++) {
            if (units[i].hasUnitNumber(number)) {
                return true;
            }
        }

        return false;
    },
    getUnits: function() {
        let maxUnits = this.get("units_to_show");
        let filter = this.collection.filter;
        let units = filter.run(this.get("units"));

        if (maxUnits && maxUnits < units.length) {
            let date = filter.getDateFilterValue();
            if (date) {
                units = units.slice(-1 * maxUnits);
            } else {
                units = units.slice(0, maxUnits);
            }
        }

        return units;
    },
    getAvailabilityLink: function() {
        const waitUnits = this.get("wait_units"),
            units = this.getUnits(),
            community = this.get("community"),
            customWaitListLink = this.get("custom_wait_list_link");
        let link = this.get("availability_link");

        if (customWaitListLink && !units.length) {
            return customWaitListLink.trim();
        }

        if (waitUnits && waitUnits.length) {
            if (community && availabilityUtils.isCoreLogicLink(community.application_link)) {
                link = community.application_link;
            } else {
                link = waitUnits[0].availability_link || link;
            }
        }

        return link.trim();
    },
    getFloorPlanName: function() {
        return this.get("name");
    },
    getVendorAmenities: function() {
        return dedup(flatten(this.get("units").pluck("vendor_amenities")));
    }
});

export {FloorPlanModel};
